import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PageHeader } from "@features/page-header/page-header";
import { PageTools } from "@features/page-tools/page-tools";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Loadable from "@react-loadable/revised";
import fp from "lodash/fp";
import moment from "moment";
import * as yup from "yup";
import { useHistory } from "react-router-dom";
import { siteMode } from "@cores/siteMode";
import AssetSearchBar from "./AssetSearchBar";
import AiWorkspaceButton from "./ui/ai-workspace-button";
import TreeViewLayout from "@components/tree/TreeViewLayout";
import AccessDenied from "../errors/Error403";
import {
    ASSET_TYPES,
    CATEGORY_TYPES,
    CONSTANTS,
    getAssetSearchIndexStatusOptions,
    getConstantValues,
    GROUPING_VIEW_TYPE,
    LIST_VIEW_TYPE,
    MEDIA_TYPES,
    PROPERTY_PANEL_TYPE,
    TABLE_LIST_TYPE,
    UNCATEGORIZED_ID,
    DND_TYPES,
} from "@constants";
import { closePropertyPanel, togglePropertyPanel } from "@modules/propertyPanel";
import { createOwnerLock, getAsset, updateAsset } from "@modules/asset";
import {
    bulkAttachCategoriesAsync,
    bulkDetachCategoriesAsync,
    clear,
    clearList,
    deleteAsset,
    getAssets,
    getIntegrateSearchAssets,
    increateAssetsDownloadCount,
    toggleActionButton,
    updateState,
} from "@modules/assets";
import { listRegisteredDomains, isAiSearchDomainSettled } from "@modules/integratedSearchWithSE-v3";
import { createDriveFolder, getFoldersList, moveFolder, updateMovedFolder } from "@modules/folderTreeView";
import { hasAnyOwnerLockBlocked, isOwnerLockBlockedForUser } from "@utils/check-owner-lock";
import AssetTable from "./AssetListView/AssetTable";
import AssetCard from "./AssetCard";

import compareVersions, {
    VERSION_CM_12725_ASSET_AUTO_ARCHIVE,
    VERSION_CM_12923_LIST_SEARCHBAR_FILTER,
    VERSION_CM_13766_SHARES_V3,
    VERSION_CM_14143_REQUEST,
    VERSION_CM_14205_ASSET_STARRED,
    VERSION_CM_15199_MULTI_ASSETS_OWNER_CHANGE,
    VERSION_CM_16963_PROJECT_SETTING_AI_SEARCH,
    VERSION_DAM_228_FOLDER_TREE_DND,
    VERSION_DAM_28_ASSET_INDEX_STATUS,
    VERSION_DAM_29_ASSET_INDEX,
    VERSION_DAM_350_ASSET_BULK_INDEX_RETRY,
} from "@cores/version";
import VersionedComponent from "@components/VersionedComponent";
import withSearchQueryParamsContext from "@components/params/withSearchQueryParamsContext";
import { loadViewInfo, saveViewInfo } from "@cores/viewStorage";
import { DRIVE_FOLDER_SEARCH_TYPE } from "../../constants";
import ListTools from "../../features/list-tools/list-tools";
import {
    IconAdd,
    IconAi,
    IconArchive,
    IconCircle,
    IconCopy,
    IconDelete,
    IconDownload,
    IconFileCopyto,
    IconIndexing,
    IconMove,
    IconStar,
    IconStarOutline,
} from "@mzc-cloudplex/icons";
import { listAssetExtensionsAPI, moveAssetsFolderAPI } from "@modules/apis/assets";
import menuService from "@services/menuService";
import { changeSelectedByList, changeSelectedItem } from "@modules/categoryTreeView";
import { useLocation } from "react-router";
import { setNotification } from "@modules/notification";
import store from "@store/store";
import TooltipButton from "@components_v2/tooltip-button/tooltip-button";
import { isAssetSearchIndexRetryable } from "@components_v2/asset-search-index-status";
import { bulkUpsertResourceIndexAPI, createSearchIndexAPI } from "@modules/apis/integrated-search-v3";
import { listJobs } from "@modules/bulkJobs";
import { Pagination } from "@components_v2/pgaination/pagination";
import { DialogCreateAsset } from "@routes/asset/dialog-create";
import DialogArchive from "src/features/dialog/dialog-archive";
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    Grid,
    ListItemButton,
    ListItemText,
    Tooltip,
    Typography,
} from "@mzc-pdc/ui";
import BreadcrumbsWithRouterPath from "../../containers/BreadcrumbsWithRouterPath/BreadcrumbsWithRouterPath";
import DialogCreateCollection from "@routes/collections/dialog-create";
import ListDragPreview from "@components/Preview/ListDragPreview";
import useSettings from "@hooks/useSettings";
import ListViewSelectButton from "@components_v2/list-view-select-button/list-view-select-button";
import DialogShare from "@features/dialog/share/dialog-share";
import DialogAssetDuplicate from "@routes/asset/dialog-asset-duplicate";
import useArchive from "@hooks/useArchive";
import useCheckPermission from "@hooks/useCheckPermission";
import { alpha } from "@mui/material";
import theme from "@styles/theme";
import useFolder from "@hooks/useFolder";
import { getContentSchemasAPI } from "@modules/apis/content-schemas";
import useVirtualFolderPath from "@hooks/useVirtualFolderPath";
import CustomMenuBreadcrumb from "@components_v2/breadcrumb/custom-menu-breadcrumb";
import countDaysBetweenDates from "@cores/countDaysBetweentDates";
import dayjs from "dayjs";
import { bulkAddFavoriteAPI, bulkRemoveFavoriteAPI } from "@modules/apis/favorite";
import { DialogRequest, DialogRequestDownload } from "@routes/request/dialog-request";
import useDownloadJobs from "@hooks/useDownloadJobs";
import DialogChangeOwner from "@routes/asset/dialog-change-owner";
import CollapsibleSplitTwoTierGridLeft from "@components_v2/grid/collapsible-split-two-tier-grid-left";
import { safeJsonParse } from "@utils/safeJsonParse";

const LoadableDeleteAssetWithReferenceModal = Loadable({
    loader: () => import("./ui/DeleteAssetWithReferenceModal"),
    loading: () => null,
});

const LoadableSelectCategoriesModal = Loadable({
    loader: () => import("@components/modals/SelectCategories"),
    loading: () => null,
});

const LoadableSelectFolderPathModal = Loadable({
    loader: () => import("../../components/modals/SelectFolderPath"),
    loading: () => null,
});

const LoadableNewFolderModal = Loadable({
    loader: () => import("../../components/modals/NewFolderModal"),
    loading: () => null,
});

const LoadableDownloadLinkModal = Loadable({
    loader: () => import("@components/modals/DownloadLinkModal"),
    loading: () => null,
});

const LoadableRenameModal = Loadable({
    loader: () => import("@components/modals/Rename"),
    loading: () => null,
});

const LoadableFolderRenameModal = Loadable({
    loader: () => import("@components/modals/FolderRenameModal"),
    loading: () => null,
});

const LoadableCreatePhotoModal = Loadable({
    loader: () => import("@routes/asset/ui/CreatePhotoModal"),
    loading: () => null,
});

const LoadableShareRequestModal = Loadable({
    loader: () => import("@components/modals/ShareRequestModal"),
    loading: () => null,
});

// indexes:bulk 는 요청당 resourceIds 100건까지만 받는다(스펙). 목록은 최대 200건까지 선택 가능해 나눠 보낸다.
const BULK_REINDEX_MAX_IDS = 100;
// 접수형 비동기 API라 즉시 조회하면 상태가 그대로다. 첫 전이가 반영될 여유를 두고 새로고침한다.
// 일괄 바 재시도와 행 ⋯ 메뉴 단건 재시도가 같은 타이머를 공유한다.
const REINDEX_REFRESH_DELAY_MS = 500;

const AssetsColumns = [
    { id: "name", label: "Name", type: "text", sortable: true },
    { id: "indexStatus", label: "AI search indexing status", type: "text", sortable: false },
    // 미리보기 추출 상태는 컬럼으로 두지 않는다 - 추출은 업로드 직후 몇 분이면 끝나는 일시적 공정이라
    // 목록 대부분의 행에서 값이 비고, 비교·정렬 축도 되지 못한다.
    // 대신 행 썸네일 좌하단 배지(AssetTableCard)로 진행 중·실패일 때만 노출한다. (DAM-457)
    { id: "size", label: "Size", type: "number", sortable: false },
    { id: "autoArchivedAt", label: "Auto-archive schedule date", type: "date", sortable: false },
    { id: "createdAt", label: "Created", type: "date", sortable: true },
    { id: "owner", label: "Owner", type: "user", sortable: false },
];

// TODO : Video_list와 동일한 형식, 컴포넌트화 필요
const AssetList = ({ searchQueryParams, location }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const { pathname } = useLocation();
    const isLibrary = siteMode.get() === "CMS";

    const { spaceId, projectId } = menuService.parse(pathname);
    const { isCardView, updateIsCardView, getSettingData, resourceSettings } = useSettings({
        type: "General",
        domain: "assets",
    });
    const { getSettingData: getSettingMenuName, resourceSettings: settingsMenuName } = useSettings({
        type: "MenuName",
        domain: "assets",
    });
    const { getVirtualFolderFullPath } = useVirtualFolderPath({});
    const ASSETS_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Assets" });
    const PHOTO_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Photo" });
    const FOLDERS_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Folders" });
    const SHARES_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Shares" });
    const DOWNLOAD_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Download" });
    const { requestArchive } = useArchive();
    const { onClickFolderTree, onRenameFolder, currentFolderInfo } = useFolder({
        domain: "ASSET",
    });
    const isCms = siteMode.get() === "CMS";
    const { generateDownloadJobHash, requestDownloadJob } = useDownloadJobs();

    const stage = useSelector(({ stage }) => stage);
    const folderTreeView = useSelector(({ folderTreeView }) => folderTreeView);
    const folderTreeViewModal = useSelector(({ folderTreeViewModal }) => folderTreeViewModal);
    const categoryTreeView = useSelector(({ categoryTreeView }) => categoryTreeView);
    const assets = useSelector(({ assets }) => assets);
    const aiSearchDomains = useSelector(
        ({ integratedSearchWithSE_V3 }) => integratedSearchWithSE_V3?.registeredDomain?.ASSET?.domains,
    );
    // AI 검색 기능 지원 버전(1.0.150+) 여부
    const isAiSearchVersion = compareVersions(stage.version, VERSION_CM_16963_PROJECT_SETTING_AI_SEARCH) >= 0;

    // 프로젝트 설정(CM-16975)에서 등록한 ASSET 도메인이 검색 준비 완료(settled) 상태일 때만 AI 모드 노출
    const aiSearchEnabled = useMemo(
        () => isAiSearchVersion && aiSearchDomains?.length > 0 && isAiSearchDomainSettled(aiSearchDomains),
        [isAiSearchVersion, aiSearchDomains],
    );

    // 자산 생성 버튼 노출 조건: CMS / 통합검색뷰에서는 숨김
    const isCreateButtonVisible = !isCms && searchQueryParams.params.activeView !== LIST_VIEW_TYPE.SEARCH;

    // const [isMountPending, setIsMountPending] = useState(true);
    const [selectedItems, setSelectedItems] = useState([]);
    const [selectedItemsDragging, setSelectedItemsDragging] = useState(false);

    const selectedItemsHasOwnerLock = useMemo(
        () => hasAnyOwnerLockBlocked(selectedItems, stage),
        [selectedItems, stage],
    );
    const [isQuickSearch, setIsQuickSearch] = useState(true);
    const [modal, setModal] = useState("");
    const [actionTarget, setActionTarget] = useState(null);
    const [isControlSticky, setIsControlSticky] = useState(false);
    const [actionPending, setActionPending] = useState({});
    const [filterOptionValues, setFilterOptionValues] = useState(null);
    const [sortBy, setSortBy] = useState({ id: null, isAsc: false });
    const [tokenPaginationInfo, setTokenPaginationInfo] = useState({ currentPage: 0, data: [] });

    const listDragPreviewRef = useRef();
    const stickyDomRef = useRef();
    const onClickRefreshRef = useRef();
    const reindexRefreshTimerRef = useRef();
    // 행 ⋯ 메뉴 단건 재시도 진행 중인 자산 id. 화면에 pending 표시가 없어 state 대신 ref로 둔다
    // (state면 useCallback deps가 흔들려 React.memo인 AssetCard 전체가 매번 리렌더된다).
    const reindexingAssetIdsRef = useRef(new Set());
    const requestDownloadJobRef = useRef();
    requestDownloadJobRef.current = requestDownloadJob;

    const handleScroll = (e) => {
        if (!stickyDomRef.current) return;
        const boxSize = stickyDomRef.current.getBoundingClientRect();
        const isSticky = boxSize.top <= window.scrollY;

        setIsControlSticky(isSticky);
    };

    const onHandleChangeSort = (params) => {
        setSortBy((prev) => ({ ...(prev ?? { id: null, isAsc: false }), ...params }));
    };

    const getFilterOptions = async (filterIds) => {
        const results = {};

        // 각 필터 ID에 대해 병렬로 처리
        await Promise.all(
            filterIds.map(async (id) => {
                switch (id) {
                    case "extensions": {
                        let extensions = [];
                        if (assets?.assetExtensions?.data?.length > 0) {
                            extensions = assets.assetExtensions.data;
                        } else {
                            const response = await listAssetExtensionsAPI(stage.id, stage.endpoint);
                            extensions = response.data;
                        }
                        results[id] = extensions.map(({ extension }) => ({
                            id: extension,
                            label: extension,
                            value: extension,
                        }));
                        break;
                    }
                    case "contentSchema": {
                        let tempResults = [];
                        let nextToken = null;
                        do {
                            const params = { status: "ACTIVE", type: "EXTENDED_METADATA", nextToken };
                            const response = await getContentSchemasAPI(stage.id, stage.endpoint, projectId, params);
                            tempResults.push(...response.data.results);
                            nextToken = response.data.nextToken;
                        } while (nextToken);
                        results[id] = tempResults.map(({ id, name }) => ({
                            id,
                            label: name,
                            value: id,
                        }));
                        break;
                    }
                }
            }),
        );

        setFilterOptionValues(results);
    };

    const getViewInfoParams = () => {
        const viewInfo = loadViewInfo("assetsViewInfo", spaceId, projectId) || {};

        if (viewInfo?.virtualFolderId && !viewInfo?.virtualDriveId) {
            delete viewInfo.virtualFolderId;
        }
        delete viewInfo?.sortBy;
        delete viewInfo?.isSortAsc;
        // 검색 뷰는 일시 상태 — 오염으로 저장된 search는 없는 값으로 취급한다.
        // 비워두면 mount 시 initActiveViewParams가 settings의 Default View Mode로 채운다 (DAM-510)
        if (viewInfo?.activeView === LIST_VIEW_TYPE.SEARCH) delete viewInfo.activeView;
        return viewInfo;
    };

    const initActiveViewParams = useCallback(
        (customMenuParams = {}) => {
            try {
                const inputParams = {
                    ...searchQueryParams.params,
                    ...customMenuParams,
                    offset: searchQueryParams.params.offset ?? 0,
                };
                const assetDefaultViewMode = resourceSettings.settings?.Asset?.defaultViewMode?.value;
                let newParams = { ...getViewInfoParams(), ...inputParams };
                let isSearch = false;
                let activeViewParams;

                if (newParams.activeView === "search") isSearch = true;

                if (assetDefaultViewMode) {
                    activeViewParams = {
                        activeView: assetDefaultViewMode !== "list" ? "hierarchy" : "list",
                        hierarchyType: assetDefaultViewMode === "list" ? "category" : assetDefaultViewMode,
                    };
                } else {
                    //Note: settings > Assets > Default View Mode가 없을 경우 리스트를 기본값으로 가져가도록 설정(Settings의 기본값과 동일하게)
                    activeViewParams = {
                        activeView: "list",
                    };
                }

                if (!newParams.activeView) {
                    newParams = { ...newParams, ...activeViewParams };
                }

                if (newParams) {
                    dispatch(
                        updateState({
                            ...assets,
                            isActiveView: newParams.activeView ?? LIST_VIEW_TYPE.LIST,
                            hierarchyType: newParams.hierarchyType,
                        }),
                    );

                    if (searchQueryParams.hierarchyType === GROUPING_VIEW_TYPE.CATEGORY) {
                        dispatch(changeSelectedByList(newParams.selectedCategoryId));
                    }
                    searchQueryParams.resetSearchQueryParams({
                        action: "replace",
                        params: newParams,
                    });

                    if (isSearch) {
                        saveAssetViewInfo({
                            ...newParams,
                            ...(getViewInfoParams().activeView ? getViewInfoParams() : activeViewParams),
                        });
                    } else saveAssetViewInfo(newParams);
                }
            } catch (error) {
                console.error(error);
            }
        },
        [spaceId, projectId, searchQueryParams.params, resourceSettings],
    );

    const onChangeSearchQueryParams = async () => {
        try {
            const categoryTreeView = mergeParams.selectedCategoryId;
            const folderViewCondition = mergeParams.virtualFolderId && mergeParams.virtualDriveId;

            if (mergeParams.activeView === LIST_VIEW_TYPE.HIERARCHY) {
                if (mergeParams.hierarchyType === GROUPING_VIEW_TYPE.CATEGORY) {
                    if (!categoryTreeView) return;
                } else {
                    if (!folderViewCondition) return;
                }
            }
            await loadAssetData(mergeParams);
        } catch (error) {
            console.error(error);
        }
    };

    const selectedCategoryIdToObject = (selectedCategoryId) => {
        const params = {};
        if (fp.isNil(selectedCategoryId) || selectedCategoryId === UNCATEGORIZED_ID) {
            params.selectedCategoryId = UNCATEGORIZED_ID;
            params.isNotCategoryIds = params.selectedCategoryId === UNCATEGORIZED_ID;
        } else {
            params.selectedCategoryId = selectedCategoryId;
            params.isNotCategoryIds = selectedCategoryId === UNCATEGORIZED_ID;
        }
        return params;
    };

    const searchBarInitialValues = useMemo(
        () => ({ ...searchQueryParams.params, activeView: LIST_VIEW_TYPE.SEARCH }),
        [searchQueryParams.params],
    );

    const isCustomMenu = useMemo(() => {
        if (!settingsMenuName || !settingsMenuName?.settings) return undefined;
        return settingsMenuName?.settings?.MenuName?.selectMenu?.value === "custom";
    }, [settingsMenuName]);

    const mergeParams = useMemo(() => {
        let params_ = {
            ...searchQueryParams.params,
        };
        if (compareVersions(stage.version, VERSION_CM_12725_ASSET_AUTO_ARCHIVE) >= 0)
            params_.withAutoArchiveDate = true;

        // 자산 조회 파라미터 중 indexDocStatus만 허용값 외를 400으로 거부한다(assets.yml). 그 400은
        // assets.error → 전체 AccessDenied라 검색바까지 사라져 사용자가 필터를 지울 수단이 없다.
        // 드롭다운은 버전으로 걸러 노출하지만 URL 파라미터는 그 게이트를 지나지 않으므로 여기서 같이 거른다.
        if (params_.indexDocStatus?.length > 0) {
            const allowedIndexStatuses = getAssetSearchIndexStatusOptions(stage.version).map((option) => option.value);
            const indexDocStatus = params_.indexDocStatus.filter((status) => allowedIndexStatuses.includes(status));
            if (indexDocStatus.length) params_.indexDocStatus = indexDocStatus;
            else delete params_.indexDocStatus;
        }

        if (params_.activeView?.toLowerCase() === LIST_VIEW_TYPE.HIERARCHY) {
            if (!fp.isNil(params_.hierarchyType))
                params_.hierarchyType = params_.hierarchyType ?? GROUPING_VIEW_TYPE.FOLDER;
            if (params_.hierarchyType === GROUPING_VIEW_TYPE.CATEGORY) {
                if (fp.isNil(params_.selectedCategoryId))
                    params_.selectedCategoryId = getViewInfoParams().selectedCategoryId;
                params_ = { ...params_, ...selectedCategoryIdToObject(params_.selectedCategoryId) };
                delete params_.virtualFolderId;
            } else {
                if (!fp.isNil(params_.selectedCategoryId)) delete params_.categoryIds;
                delete params_.selectedCategoryId;
                delete params_.isNotCategoryIds;
            }
        } else {
            // TODO: CustomMenu에서는 초기화 해줄 필요가 없음
            if (!isCustomMenu) {
                if (!fp.isNil(params_.selectedCategoryId)) delete params_.categoryIds;
                delete params_.selectedCategoryId;
                delete params_.isNotCategoryIds;
                delete params_.virtualFolderId;
            }
        }

        return params_;
    }, [spaceId, projectId, searchQueryParams.params, settingsMenuName, stage.version]);

    // 행 ⋯ 메뉴 단건 재시도 노출 조건. 일괄 바(hidden={isCms || !aiSearchEnabled})와 같은 축이고,
    // 단건 API(DAM-29, 1.0.151+)를 쓰므로 벌크(DAM-350, 1.0.153+)보다 버전 게이트만 낮다.
    // AI 검색(별도 라우트 list-ai-search)은 v3 search의 resource 스키마가 불투명 객체라 indexDoc 포함이
    // 보장되지 않아 노출하지 않는다(검색엔진 v2 경로는 응답에 indexDoc이 required로 포함되므로 제외하지 않는다).
    const isRowReindexVisible =
        !isCms && aiSearchEnabled && compareVersions(stage.version, VERSION_DAM_29_ASSET_INDEX) >= 0;

    const saveAssetViewInfo = (queryParams) => {
        const stored = getViewInfoParams();

        // 검색 뷰는 일시 상태라 불필요한 값이 합쳐지지 않도록 한다. — 직전 뷰(트리 위치 포함)는 그대로 두고 limit만 갱신
        if (queryParams.activeView === LIST_VIEW_TYPE.SEARCH) {
            saveViewInfo("assetsViewInfo", {
                ...stored,
                stageId: spaceId,
                projectId: projectId,
                limit: queryParams.limit,
            });
            return;
        }

        saveViewInfo("assetsViewInfo", {
            stageId: spaceId,
            projectId: projectId,
            activeView: queryParams.activeView,
            hierarchyType: queryParams.hierarchyType,
            selectedCategoryId: queryParams.selectedCategoryId,
            virtualFolderId: queryParams.virtualFolderId,
            virtualDriveId: queryParams.virtualDriveId,
            // 사용자가 직접 고른 값만 유지한다. 선택 전이면 undefined 라 키가 빠지고, 기본값은 settings 에서 파생된다.
            isCardView: stored.isCardView,
            limit: queryParams.limit,
        });
    };

    const onClickTreeItem = (item, isList) => {
        setSelectedItems([]);

        const activeView = searchQueryParams.params.activeView || assets.activeView;
        let newParams = {};
        if (searchQueryParams.params.hasOwnProperty("enableFolderView"))
            newParams.enableFolderView = searchQueryParams.params.enableFolderView;
        if (searchQueryParams.params.hasOwnProperty("enableCategoryView"))
            newParams.enableCategoryView = searchQueryParams.params.enableCategoryView;
        if (compareVersions(stage.version, VERSION_CM_12725_ASSET_AUTO_ARCHIVE) >= 0) {
            newParams.withAutoArchiveDate = true;
        }
        if (searchQueryParams.params.isNotIngestType)
            newParams.isNotIngestType = searchQueryParams.params.isNotIngestType;

        if (item.hierarchyType !== GROUPING_VIEW_TYPE.FOLDER) {
            const categoryTreeView = store.getState().categoryTreeView;
            newParams = {
                ...newParams,
                categoryGroupId: item.id !== UNCATEGORIZED_ID && item.isRoot ? item.id : "",
                selectedCategoryId: item.id,
                isNotCategoryIds: item.id === UNCATEGORIZED_ID,
                limit: searchQueryParams.params.limit,
                offset: 0,
                hierarchyType: item.hierarchyType,
                activeView,
            };
            if (item.id === UNCATEGORIZED_ID) {
                searchQueryParams.params.categoryGroupId = categoryTreeView.data?.[0]?.id ?? "";
            }

            dispatch(
                changeSelectedItem({
                    id: newParams.selectedCategoryId,
                }),
            );
        } else {
            newParams = {
                ...newParams,
                virtualDriveId: item.driveId,
                virtualFolderId: item.hierarchyType === GROUPING_VIEW_TYPE.FOLDER ? item.id : "",
                hierarchyType: item.hierarchyType,
                activeView,
            };
        }
        saveAssetViewInfo({ ...newParams });

        if (searchQueryParams.params.selectedContentSchemaId)
            newParams.selectedContentSchemaId = searchQueryParams.params.selectedContentSchemaId;

        if (activeView?.toLowerCase() === LIST_VIEW_TYPE.HIERARCHY && !isList) {
            const isInitialRender =
                !searchQueryParams.params.virtualFolderId && !searchQueryParams.params.selectedCategoryId;

            searchQueryParams.resetSearchQueryParams({
                action: isInitialRender ? "replace" : "push",
                params: newParams,
            });
        }
    };

    const onClickToggleActionButton = useCallback(
        (asset) => {
            const isCreatableVideo =
                asset.status === "ACTIVE" &&
                asset.mediaType === MEDIA_TYPES.VIDEO &&
                asset.type !== ASSET_TYPES.DASH &&
                asset.type !== ASSET_TYPES.HLS;
            dispatch(
                toggleActionButton({
                    id: asset.id,
                    disabledCreateJob: !isCreatableVideo,
                }),
            );
        },
        [dispatch],
    );

    const onChangeOffset = async (page) => {
        searchQueryParams.updateSearchQueryParams({
            action: "push",
            params: {
                offset: page,
            },
        });
    };

    const onChangeLimit = (limit) => {
        searchQueryParams.updateSearchQueryParams({
            action: "push",
            params: { offset: 0, limit },
        });
        saveAssetViewInfo({ ...searchQueryParams.params, limit });
    };

    const onClickActiveView = (value) => {
        dispatch(updateState(value));
    };

    const onClickViewType = (value) => {
        if (value === isCardView) return;
        updateIsCardView(value);
    };

    const onClickEnterAiSearch = useCallback(() => {
        history.push(
            isCustomMenu ? `${pathname}/ai-search` : menuService.paths.assetsAssetsAiSearch(spaceId, projectId),
        );
    }, [history, isCustomMenu, pathname, spaceId, projectId]);

    const onClickRow = useCallback(
        (id, projectId) => {
            if (isLibrary) return;
            dispatch(
                togglePropertyPanel({
                    targetId: id,
                    type: PROPERTY_PANEL_TYPE.ASSET,
                    projectId,
                }),
            );
        },
        [isLibrary, dispatch],
    );

    const onChangeSelectItem = useCallback((asset) => {
        setSelectedItems((prev) => {
            if (prev.find((item) => item.id === asset.id)) {
                return prev.filter((v) => v.id !== asset.id);
            }
            return [...prev, asset];
        });
    }, []);

    const onChangeNumSelected = useCallback(
        (checkAll) => {
            if (checkAll) {
                const filteredAssetsData = assets.data?.filter((el) => el.state !== "DRAFT");
                setSelectedItems([].concat(filteredAssetsData));
            } else {
                setSelectedItems([]);
            }
        },
        [assets.data, setSelectedItems],
    );

    const onClickDeleteCategories = async (isDeleteAssets) => {
        if (isDeleteAssets) {
            for (let i = 0; i < selectedItems.length; i++) {
                const ele = selectedItems[i];
                await dispatch(deleteAsset(ele.id, ele.version));
            }
        } else {
            await dispatch(bulkDetachCategoriesAsync(selectedItems, searchQueryParams.params.categoryIds));
        }
        loadAssetData(mergeParams);
        setSelectedItems([]);
    };

    const onChangeCategoriesCopyToItems = async (categories) => {
        const items = actionTarget ? actionTarget : selectedItems;
        if (items && !items.length) throw new Error(`Invalid Parameters, Please check the attached file`);

        await dispatch(bulkAttachCategoriesAsync(items, categories));
        setSelectedItems([]);
        setActionTarget(null);
    };

    const onChangeCategoriesMoveToItems = async (categories) => {
        const items = actionTarget ? actionTarget : selectedItems;
        if (items && !items.length) throw new Error(`Invalid Parameters, Please check the attached file`);

        const prevCategories = [{ id: searchQueryParams.params.selectedCategoryId }];

        const categoryIds = [];
        categories.map((v) => {
            if (!prevCategories.map((p) => p.id).includes(v.id)) {
                if (v.id !== UNCATEGORIZED_ID) categoryIds.push({ id: v.id });
                else categoryIds.push({ id: v.id });
            }
        });

        const detachCategories = !categories.map((v) => v.id).includes(searchQueryParams.params.selectedCategoryId)
            ? prevCategories
            : null;

        if (detachCategories) {
            await dispatch(bulkDetachCategoriesAsync(items, prevCategories));
        }
        if (categoryIds.length > 0) await dispatch(bulkAttachCategoriesAsync(items, categoryIds));
    };

    const onHandleClickChangeAssetsFolder = async (path) => {
        const targets = actionTarget ?? selectedItems;
        const data = { assetIds: targets.map((item) => item.id), virtualFolder: { id: path } };

        await moveAssetsFolderAPI(spaceId, stage.endpoint, projectId, data);
        onToggleModal();
        afterMutationWorks("move folder");
    };

    const onClickOwnerLock = useCallback(
        async (asset) => {
            try {
                const actionKey = `owner-lock-${asset.id}`;
                if (actionPending[actionKey]) return;

                setActionPending((prev) => ({ ...prev, [actionKey]: true }));

                const params = { assetIds: [asset.id] };
                await dispatch(createOwnerLock({ isLocked: asset.isOwnerLocked, params }));
                await dispatch(
                    setNotification({
                        type: "success",
                        contents: asset.isOwnerLocked
                            ? t("common::msg::Asset lock has been disabled.", "Asset lock has been disabled.")
                            : t("common::msg::Asset lock has been enabled.", "Asset lock has been enabled."),
                    }),
                );
                onClickRefreshRef.current?.();
                setActionPending((prev) => ({ ...prev, [actionKey]: false }));
            } catch (err) {
                console.log(err);
            }
        },
        [actionPending, dispatch, t],
    );

    const onClickDeleteWithReference = async (targetItem, isEndItem, isDeleteCategory = false) => {
        if (!isDeleteCategory) {
            const r = await dispatch(deleteAsset(targetItem.id, targetItem.version));
        } else {
            await dispatch(
                bulkDetachCategoriesAsync([targetItem], [{ id: searchQueryParams.params.selectedCategoryId }]),
            );
        }
    };

    const onClickDeleteAll = async (remainItems, isDeleteCategory = false) => {
        if (!isDeleteCategory) {
            for (let i = 0; i < remainItems.length; i++) {
                const item = remainItems[i];
                const r = await dispatch(deleteAsset(item.id, item.version));
            }
        } else {
            await dispatch(
                bulkDetachCategoriesAsync(remainItems, [{ id: searchQueryParams.params.selectedCategoryId }]),
            );
        }
    };

    const onDeleteEnd = async () => {
        await loadAssetData(mergeParams);

        setSelectedItems([]);
        onToggleModal();
    };

    const loadAssetData = async (searchParam) => {
        try {
            const searchQuery = { ...searchParam };

            if (searchQuery.selectedCategoryId) {
                if (searchQuery.selectedCategoryId === UNCATEGORIZED_ID) searchQuery.categoryIds = [{ id: -1 }];
                else searchQuery.categoryIds = [{ id: searchQuery.selectedCategoryId }];
            }
            if (searchQuery.selectedContentSchemaId) {
                searchQuery.contentSchema = { id: searchQuery.selectedContentSchemaId };
            }
            if (compareVersions(stage.version, VERSION_CM_13766_SHARES_V3) >= 0) {
                searchQuery.includeShareInfo = true;
            }

            if (
                searchParam.activeView === LIST_VIEW_TYPE.SEARCH &&
                resourceSettings.settings?.Asset?.useSearchEngine?.value === "true" &&
                stage?.extensions?.["SEARCH_ENGINE_ENABLED"] === "true"
            ) {
                const offset = searchQueryParams.params.offset ?? 0;
                searchQuery.nextToken = offset > 0 ? tokenPaginationInfo.data[offset - 1] : undefined;
                searchQuery.projectIds = [{ id: projectId }];
                const results = await dispatch(getIntegrateSearchAssets(projectId, searchQuery));
                loadCallbackFunc(results?.nextToken);
            } else {
                await dispatch(getAssets(projectId, searchQuery));
            }
        } catch (e) {
            console.log(e);
        }
    };

    const loadCallbackFunc = useCallback(
        (nextToken, init) => {
            if (!nextToken) return;
            const page = init ? 0 : mergeParams?.offset;
            const copiedTokens = init ? [] : [...tokenPaginationInfo.data];
            copiedTokens[page] = nextToken ?? null;
            setTokenPaginationInfo({ currentPage: page, data: copiedTokens });
        },
        [mergeParams, tokenPaginationInfo],
    );

    const getLinkDestination = useCallback(
        (asset) => {
            if (isCms) {
                if (isCustomMenu)
                    return `${menuService.paths.library.customMenuSets.assetsAssetDetail(
                        spaceId,
                        projectId,
                        asset.id,
                    )}?projectId=${asset.project?.id}`;
                else
                    return `${menuService.paths.library.assetsAssetDetail(spaceId, projectId, asset.id)}?projectId=${
                        asset.project.id
                    }`;
            } else {
                if (isCustomMenu)
                    return `${menuService.paths.customMenuSets.assetsAssetDetail(
                        spaceId,
                        projectId,
                        asset.id,
                    )}?projectId=${asset.project?.id}`;
                else
                    return `${menuService.paths.assetsAssetDetail(spaceId, projectId, asset.id)}?projectId=${
                        asset.project?.id
                    }`;
            }
        },
        [spaceId, projectId, isCustomMenu, isCms],
    );

    const onDoubleClickRow = useCallback(
        (asset) => {
            history.push(getLinkDestination(asset));
        },
        [history, getLinkDestination],
    );

    const onDragStartItem = useCallback(
        (e) => {
            if (selectedItems && selectedItems.length) {
                e.dataTransfer.setDragImage(listDragPreviewRef.current, 0, 0);
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData(DND_TYPES.LIST_ITEM, "");

                setSelectedItemsDragging(true);
            }
        },
        [selectedItems],
    );

    const onDragEndItem = useCallback(() => {
        setSelectedItemsDragging(false);
    }, []);

    // 다중 선택 에셋을 트리(폴더/카테고리)에 드래그앤드롭 이동
    const onDropTreeItem = async (item) => {
        if (!item) return;

        const targets = actionTarget ?? selectedItems;
        if (!targets?.length) return;

        const isFolderMode = assets?.hierarchyType === GROUPING_VIEW_TYPE.FOLDER;

        try {
            if (isFolderMode) {
                // 현재 폴더로 드롭한 경우 무시
                if (item.id === searchQueryParams.params.virtualFolderId) return;
                await onHandleClickChangeAssetsFolder(item.id);
            } else {
                // 카테고리 그룹/루트 노드는 트리 측에서 이미 제외됨
                if (item.id === searchQueryParams.params.selectedCategoryId) return;
                await onChangeCategoriesMoveToItems([item]);
                afterMutationWorks("move");
            }
        } catch (error) {
            console.error("Failed to move on drop", error);
            const errorMsg = error?.response?.data?.message;
            const baseMsg = t("common::msg::Failed to move.", "Failed to move.");
            dispatch(
                setNotification({
                    type: "error",
                    contents: errorMsg ? `${baseMsg} - ${errorMsg}` : baseMsg,
                }),
            );
        } finally {
            setSelectedItemsDragging(false);
        }
    };

    const onClickCreateJob = useCallback(
        (asestId) => {
            const { spaceId, projectId } = menuService.parse(location.pathname);
            const queryParameter = `assetId=${asestId}`;

            history.push(`${menuService.paths.contentsVideoCreate(spaceId, projectId)}?${queryParameter}`);
        },
        [history, location.pathname],
    );

    const openImageViewer = () => {
        // const { assets, location, categoryTreeView } = this.props;
        const { spaceId, projectId } = menuService.parse(location.pathname);

        const isQuickSearch = fp.get(`search.isQuickSearch`, assets);

        const composer = fp.pipe(
            fp.get(`search`),
            (object) => {
                const convertMomentToString = (dateTime) => {
                    return `${moment(`${dateTime.format("YYYY-MM-DD")} 00:00:00`)
                        .utc()
                        .format("YYYY-MM-DD")}T${moment(`${dateTime.format("YYYY-MM-DD")} 00:00:00`)
                        .utc()
                        .format("HH:mm:ss")}Z`;
                };

                const search = fp.cloneDeep(object);
                if (!isQuickSearch) {
                    delete search.keyword;
                }

                if (!fp.isEmpty(fp.get("startAt", search)) && !fp.isEmpty(fp.get("endAt", search))) {
                    search.createdAt = `${convertMomentToString(fp.get("startAt", search))}~${convertMomentToString(
                        fp.get("endAt", search),
                    )}`;
                    delete search.startAt;
                    delete search.endAt;
                }

                return search;
            },
            fp.omitBy(fp.isEmpty),
            (object) =>
                fp.pipe(
                    fp.keys,
                    fp.map((key) => {
                        const value = object[key];
                        const arrayJoin = fp.pipe(
                            fp.map((item) =>
                                fp.has([`value`], item) || fp.has([`id`], item) // item 에 value 또는 id 키가 있는지 여부
                                    ? fp.getOr(fp.get(`value`, item), `id`, item)
                                    : item,
                            ),
                            fp.join(`,`),
                        );

                        const replaceValue = fp.isArray(value) ? arrayJoin(value) : value;
                        const replaceKey = fp.cond([
                            [fp.isEqual(`keyword`), () => `q`],
                            [fp.isEqual(`mediaTypes`), () => `mediaType`],
                            [fp.T, (value) => value],
                        ]);
                        // return `${fp.isEqual(`keyword`, key) ? `q` : [key]}=${replaceValue}`
                        // return `${[replaceKey(key)]}=${replaceValue}`
                        //TODO: 한개 타입만 허용(다시 물어봐야함)
                        return `${[replaceKey(key)]}=${
                            fp.isEqual(`mediaTypes`, key)
                                ? fp.includes(`,`, replaceValue)
                                    ? fp.pipe(fp.head, fp.get(`value`))(value)
                                    : replaceValue
                                : replaceValue
                        }`;
                    }),
                    fp.join(`&`),
                )(object),
        );
        const payload = {
            hierarchy: fp.getOr({}, `hierarchy`, assets),
            isActiveView: fp.get(`isActiveView`, assets),
        };
        window.open(`${menuService.paths.imagesViewer(spaceId, projectId)}?${location?.search || ""}`);
    };

    const onClickRefresh = async (e) => {
        e?.preventDefault();
        e?.stopPropagation();
        await setTokenPaginationInfo((prevState) => ({ currentPage: 1, data: [] }));
        await loadAssetData(mergeParams);
    };
    onClickRefreshRef.current = onClickRefresh;

    // 폴더뷰 클릭 (행·캐럿·브레드크럼·히스토리 공통)
    const onHandleClickFolderTree = (item, isClickArrow, checkUseHistory = false, options) => {
        onClickFolderTree(item, isClickArrow, checkUseHistory, onClickTreeItem, options);
    };

    const onClickCreateNewFolder = async (name, folder) => {
        try {
            const params = { name, parent: { id: folder.id } };
            const driveId = folder.driveId;

            await dispatch(createDriveFolder(driveId, params));
            const queryParameters = {
                parentId: folder.id,
                searchType: DRIVE_FOLDER_SEARCH_TYPE.DIRECT_CHILDREN,
            };
            const headerParams = { projectId: projectId };
            await dispatch(
                getFoldersList({
                    driveId,
                    queryParameters,
                    headerParams,
                }),
            );
        } catch (error) {
            return Promise.reject(error);
        }
    };

    const handleClickRenameAsset = async (target, value) => {
        const response = await dispatch(
            updateAsset(target.id, target.version, {
                name: value,
            }),
        );
        return response;
    };

    const afterMutationWorks = (action) => {
        setSelectedItems([]);
        setActionTarget(null);
        if (action)
            dispatch(
                setNotification({
                    type: "success",
                    contents: t(`common::msg::${"{{action}} completed."}`, {
                        action: t(`common::label::${action}`, action),
                    }),
                }),
            );
        onClickRefresh();
    };

    const onToggleModal = useCallback((key, value, actionTarget = null) => {
        if (value) {
            setModal(key);
            setActionTarget(actionTarget ? [actionTarget] : null);
        } else {
            setModal("");
            setActionTarget(null);
        }
    }, []);

    const onHandleClickDownload = useCallback(
        async (asset) => {
            const key = asset.id;
            try {
                const downloadRequestTargets = {
                    resourceId: asset?.id,
                    projectId: projectId,
                    resourceName: asset?.name,
                };

                await requestDownloadJobRef.current?.("ASSET", downloadRequestTargets, 1, false, projectId, key);
                dispatch(increateAssetsDownloadCount([asset]));
            } catch (error) {
                console.log(error);
            }
        },
        [projectId, dispatch],
    );

    const onHandleClickMultiDownload = async () => {
        if (!selectedItems?.length) return;

        try {
            const key = generateDownloadJobHash(selectedItems.map((item) => item.id));

            const downloadRequestTargets = {
                resourceId: selectedItems.length === 1 ? selectedItems[0].id : undefined,
                resourceIds: selectedItems.length > 1 ? selectedItems.map((item) => item.id) : undefined,
                projectId: projectId,
                resourceName: selectedItems[0].name,
            };

            await requestDownloadJob("ASSET", downloadRequestTargets, selectedItems.length, false, projectId, key);
            dispatch(increateAssetsDownloadCount(selectedItems));
            setSelectedItems([]);
        } catch (error) {
            console.log(error);
        }
    };

    const onHandleClickArchiveAsset = async (isFolder, option) => {
        try {
            setActionPending({ ...actionPending, archive: true });

            let targets = {};
            if (isFolder) {
                targets = {
                    virtualFolder: {
                        id: folderTreeView.folderList.selectedFolder.folder.id,
                        recursive: false, //TODO: 하위폴더 포함 여부 추가
                    },
                };
            } else {
                const ids = selectedItems?.length ? selectedItems.map((i) => i.id) : [actionTarget.id];
                targets = { ids };
            }
            const data = {
                archiveClass: option,
                targets,
            };
            const isMulti = selectedItems?.length > 1 ? true : false;

            await requestArchive(isMulti, data, async () => {
                dispatch(listJobs());
                await onClickRefresh();
                setSelectedItems([]);
            });
        } catch (e) {
            console.error(e);
        } finally {
            setActionPending({ ...actionPending, archive: false });
            onToggleModal();
        }
    };

    const onChangeFolderPath = useCallback(async () => {
        const selectedFolderByMenu = folderTreeView.folderList.selectedFolder.folder;

        const toFolder = folderTreeViewModal.folderList.selectedFolder.folder;
        const fromObj = { id: selectedFolderByMenu?.id, parent: { id: selectedFolderByMenu?.parent } };
        const toObj = { id: toFolder.id, parent: { id: toFolder.parent } };
        const params = { from: fromObj, to: toObj };
        const driveId = { driveId: selectedFolderByMenu?.driveId };

        try {
            if (toFolder.id !== fromObj.id) {
                await dispatch(moveFolder(driveId, params));

                dispatch(
                    setNotification({
                        type: "success",
                        contents: t(`common::msg::${"Move completed from {{item1}} to {{item2}}."}`, {
                            item1: selectedFolderByMenu.name,
                            item2: toFolder.name,
                        }),
                    }),
                );
            }
            const folderList = folderTreeView.folderList;

            if (!(folderList.selectedFolder.folder.id === fromObj.id && !folderList.data[toFolder.id])) {
                await dispatch(updateMovedFolder({ fromPosition: fromObj, toPosition: toObj }));
            } else {
                //Note: 현재 선택한 폴더를 이동하는데 트리뷰에 아직 정보가 없는 폴더의 경우 루트폴더로 이동

                const drive = folderTreeView.driveList.data?.find((el) => el.resourceType?.type === "ASSET");
                const rootFolder = folderList?.data?.[drive.rootFolder.id];
                onHandleClickFolderTree(rootFolder);
            }

            onToggleModal();
        } catch (error) {
            const errorData = error && error.response && error.response.data;
            const errorMessage = errorData && errorData.message;
            const errorStatus = (error && error.response && error.response.status) || "Unknown";
            const errorContents = `- ${errorMessage ?? error}`;

            dispatch(
                setNotification({
                    type: "error",
                    contents:
                        t(`common::msg::${"Error moving from {{item1}} to {{item2}}."}`, {
                            item1: selectedFolderByMenu.name,
                            item2: toFolder.name,
                        }) + errorContents,
                }),
            );
        }
    }, [folderTreeView, folderTreeViewModal]);

    const onHandleClickCreate = () => {
        onToggleModal("create", true);
    };

    const handleClickImageAssetToPhoto = useCallback(async () => {
        if (!selectedItems || selectedItems.length > 1) return;
        const [item] = selectedItems;

        const response = await dispatch(getAsset(item.id));
        if (!response?.data) {
            return;
        }

        const photoAsset = response.data;
        history.push(`${menuService.paths.contentsPhotoCreate(spaceId, projectId)}`, {
            photoAsset,
        });
    }, [spaceId, projectId, selectedItems]);

    const onHandleClickStarred = useCallback(
        async (resourceIds, isStarred) => {
            const pendingId = resourceIds?.length > 1 ? "bulk" : resourceIds[0].id;
            try {
                if (actionPending[`starred-${pendingId}`]) {
                    return;
                }
                setActionPending(`starred-${pendingId}`, true);

                const data = {
                    domain: "ASSET",
                    resourceIds: resourceIds,
                };

                if (isStarred) {
                    await bulkRemoveFavoriteAPI(stage.endpoint, projectId, data);
                } else {
                    await bulkAddFavoriteAPI(stage.endpoint, projectId, data);
                }
                setActionPending({ ...actionPending, [`starred-${pendingId}`]: false });
                afterMutationWorks();
            } catch (error) {
                console.error(error);
                setActionPending({ ...actionPending, [`starred-${pendingId}`]: false });
            }
        },
        [actionPending, mergeParams],
    );

    const onHandleClickBulkReindex = useCallback(async () => {
        if (!selectedItems?.length || actionPending["bulkReindex"]) return;

        try {
            setActionPending((prev) => ({ ...prev, bulkReindex: true }));

            const resourceIds = selectedItems.map((item) => item.id);
            const requests = [];
            for (let i = 0; i < resourceIds.length; i += BULK_REINDEX_MAX_IDS) {
                requests.push(
                    bulkUpsertResourceIndexAPI(stage.id, stage.endpoint, projectId, {
                        domain: "ASSET",
                        resourceIds: resourceIds.slice(i, i + BULK_REINDEX_MAX_IDS),
                    }),
                );
            }
            const results = await Promise.allSettled(requests);
            results.filter(({ status }) => status === "rejected").forEach(({ reason }) => console.error(reason));

            // 청크 일부만 실패해도 접수된 건은 이미 인덱싱 중이므로 선택 해제·갱신까지 진행한다.
            // 여기서 멈추면 사용자가 재클릭해 성공분을 중복 접수한다. 실패 토스트는 axios 인터셉터가 전역 처리.
            if (results.every(({ status }) => status === "rejected")) return;

            dispatch(
                setNotification({
                    type: "success",
                    contents: t(`common::msg::Reindexing has been requested.`, `Reindexing has been requested.`),
                }),
            );
            // 선택은 페이지·정렬 변경에도 유지되므로 afterMutationWorks를 캡처하면 낡은 mergeParams로 갱신될 수 있다.
            // 항상 최신 값을 가리키는 onClickRefreshRef를 쓴다.
            setSelectedItems([]);
            setActionTarget(null);
            // 행 ⋯ 메뉴 재시도와 타이머를 공유한다. 앞서 예약된 새로고침이 있으면 취소해
            // 연속 재시도가 목록 전체 재조회를 중복 트리거하지 않게 한다.
            clearTimeout(reindexRefreshTimerRef.current);
            reindexRefreshTimerRef.current = setTimeout(() => onClickRefreshRef.current?.(), REINDEX_REFRESH_DELAY_MS);
        } finally {
            setActionPending((prev) => ({ ...prev, bulkReindex: false }));
        }
    }, [selectedItems, actionPending, stage, projectId, dispatch, t]);

    // 행 ⋯ 메뉴 단건 재시도. 확인 다이얼로그 없이 즉시 실행한다(일괄 바·자산 상세 ⋯ 메뉴와 동일).
    // 낙관적 상태 갱신은 하지 않는다 - 접수형 비동기라 롤백 기준이 없어 지연 재조회로 확정한다.
    const onClickRowReindex = useCallback(
        async (asset) => {
            const assetId = asset?.id;
            if (!assetId || reindexingAssetIdsRef.current.has(assetId)) return;

            try {
                reindexingAssetIdsRef.current.add(assetId);
                // 접수형 비동기라 응답 본문으로 성패를 가르지 않는다(일괄 바와 동일) - 2xx면 요청이 받아들여진 것이다.
                await createSearchIndexAPI(stage.id, stage.endpoint, projectId, {
                    domain: "ASSET",
                    resourceId: assetId,
                });

                dispatch(
                    setNotification({
                        type: "success",
                        contents: t(`common::msg::Reindexing has been requested.`, `Reindexing has been requested.`),
                    }),
                );
                // 지연 재조회로 첫 상태 전이를 서버 값으로 확정한다.
                clearTimeout(reindexRefreshTimerRef.current);
                reindexRefreshTimerRef.current = setTimeout(
                    () => onClickRefreshRef.current?.(),
                    REINDEX_REFRESH_DELAY_MS,
                );
            } catch (error) {
                console.error(error);
                dispatch(
                    setNotification({
                        type: "error",
                        contents: t(`common::msg::Failed to request reindexing.`, `Failed to request reindexing.`),
                    }),
                );
            } finally {
                reindexingAssetIdsRef.current.delete(assetId);
            }
        },
        [stage.id, stage.endpoint, projectId, dispatch, t],
    );

    const hideContents = useMemo(() => {
        return stage && stage.extensions && stage.extensions["HIDE_CONTENTS"] === "true";
    }, [stage]);

    const getListToolsButtons = useCallback(
        (isResponsive) => {
            const isListView = assets?.isActiveView?.toLowerCase() === LIST_VIEW_TYPE.LIST;
            const isFolderView = assets?.hierarchyType === GROUPING_VIEW_TYPE.FOLDER;
            const isCategoryView = !isFolderView;
            const isMoveButtonActive = !isListView;
            const isCopyButtonActive =
                assets.isActiveView?.toLowerCase() === LIST_VIEW_TYPE.HIERARCHY &&
                assets.hierarchyType === GROUPING_VIEW_TYPE.CATEGORY;

            const checkDisabledByOwnerLock = hasAnyOwnerLockBlocked(selectedItems, stage);

            const checkDisabledDownload = selectedItems.some((item) => {
                const isExpired = item?.shareInfo?.expiresAt
                    ? countDaysBetweenDates({ toDate: dayjs(item?.shareInfo?.expiresAt) }) < 0
                    : false;

                const isExceedDownloadCount =
                    item?.shareInfo?.maxDownloads > 0 &&
                    item?.shareInfo?.maxDownloads <= item?.shareInfo?.downloadCount;

                const hasDownloadPermissionByShare = item?.shareInfo && !(isExpired || isExceedDownloadCount);

                const isDisabledByOwnerLock = isOwnerLockBlockedForUser(item, stage);

                return (
                    (!item?.shareInfo || !hasDownloadPermissionByShare) &&
                    (!DOWNLOAD_PERMISSIONS.DOWNLOAD || isDisabledByOwnerLock)
                );
            });

            // 선택이 전부 재시도 대상일 때만 활성. 판정은 행 ⋯ 메뉴와 같은 함수를 쓴다(DAM-590 AC: 일괄 바와 동일 로직).
            const checkDisabledBulkReindex =
                !selectedItems.length || !selectedItems.every((item) => isAssetSearchIndexRetryable(item?.indexDoc));

            return [
                <TooltipButton
                    key={"Download"}
                    circled
                    title={t("common::label::Download", "Download")}
                    buttonText={t("common::label::Download", "Download")}
                    size={"small"}
                    onClickButton={() => {
                        onHandleClickMultiDownload();
                    }}
                    buttonIcon={<IconDownload size={16} />}
                    isDivided={false}
                    isResponsive={isResponsive}
                    disabled={!DOWNLOAD_PERMISSIONS.DOWNLOAD}
                    hidden={compareVersions(stage.version, VERSION_CM_14143_REQUEST) >= 0 && checkDisabledDownload}
                />,
                <React.Fragment key={"Copy"}>
                    {isCopyButtonActive && (
                        <TooltipButton
                            circled
                            title={t("common::label::Copy", "Copy")}
                            buttonText={t("common::label::Copy", "Copy")}
                            size={"small"}
                            onClickButton={() => {
                                onToggleModal("copyCategory", true);
                            }}
                            buttonIcon={<IconCopy size={16} />}
                            isDivided={!isMoveButtonActive}
                            isResponsive={isResponsive}
                            disabled={!ASSETS_PERMISSIONS.EDIT || checkDisabledByOwnerLock}
                            hidden={isCms}
                        />
                    )}
                </React.Fragment>,
                <React.Fragment key={"Move"}>
                    {isMoveButtonActive && (
                        <TooltipButton
                            circled
                            title={t("common::label::Move", "Move")}
                            buttonText={t("common::label::Move", "Move")}
                            size={"small"}
                            onClickButton={() => {
                                if (isCategoryView) onToggleModal("moveCategory", true);
                                else onToggleModal("moveFolder", true);
                            }}
                            buttonIcon={<IconMove size={16} />}
                            isDivided={false}
                            isResponsive={isResponsive}
                            disabled={!ASSETS_PERMISSIONS.EDIT || (isCategoryView && checkDisabledByOwnerLock)}
                            hidden={isCms}
                        />
                    )}
                </React.Fragment>,
                <TooltipButton
                    key={"Duplicate"}
                    circled
                    title={t("common::label::Duplicate", "Duplicate")}
                    buttonText={t("common::label::Duplicate", "Duplicate")}
                    size={"small"}
                    onClickButton={() => {
                        onToggleModal("duplicate", true);
                    }}
                    buttonIcon={<IconFileCopyto size={16} />}
                    isResponsive={isResponsive}
                    disabled={!ASSETS_PERMISSIONS.EDIT || checkDisabledByOwnerLock}
                    hidden={isCms}
                />,
                <VersionedComponent version={VERSION_DAM_350_ASSET_BULK_INDEX_RETRY} key={"RetryIndexing"}>
                    <TooltipButton
                        circled
                        title={t("common::label::Retry indexing", "Retry indexing")}
                        buttonText={t("common::label::Retry indexing", "Retry indexing")}
                        size={"small"}
                        onClickButton={() => {
                            onHandleClickBulkReindex();
                        }}
                        buttonIcon={<IconIndexing size={16} />}
                        isDivided={false}
                        isResponsive={isResponsive}
                        disabled={!ASSETS_PERMISSIONS.EDIT || actionPending["bulkReindex"] || checkDisabledBulkReindex}
                        hidden={isCms || !aiSearchEnabled}
                    />
                </VersionedComponent>,
                <TooltipButton
                    key={"Delete"}
                    circled
                    title={t("common::label::Delete", "Delete")}
                    buttonText={t("common::label::Delete", "Delete")}
                    size={"small"}
                    onClickButton={() => {
                        onToggleModal("delete", true);
                    }}
                    buttonIcon={<IconDelete size={16} />}
                    isDivided={false}
                    isResponsive={isResponsive}
                    disabled={!ASSETS_PERMISSIONS.EDIT || checkDisabledByOwnerLock}
                    hidden={isCms}
                />,
                <VersionedComponent version={VERSION_CM_13766_SHARES_V3} key={"Request"}>
                    <TooltipButton
                        circled
                        title={t(`common::label::Request`, `Request`)}
                        buttonText={t(`common::label::Request`, `Request`)}
                        size={"small"}
                        onClickButton={() => {
                            onToggleModal("request", true);
                        }}
                        buttonIcon={<IconDownload size={16} />}
                        isDivided={false}
                        isResponsive={isResponsive}
                        hidden={!isCms}
                    />
                </VersionedComponent>,
                <VersionedComponent version={VERSION_CM_14205_ASSET_STARRED} key={"Starred"}>
                    <TooltipButton
                        circled
                        title={t(`common::label::Starred`, `Starred`)}
                        buttonText={t(`common::label::Starred`, `Starred`)}
                        size={"small"}
                        onClickButton={() => {
                            onHandleClickStarred(
                                selectedItems.map((item) => item.id),
                                false,
                            );
                        }}
                        buttonIcon={<IconStar size={16} />}
                        isDivided={false}
                        isResponsive={isResponsive}
                        disabled={!ASSETS_PERMISSIONS.READ}
                        hidden={!isCms || !selectedItems.some((item) => !item.myFavorite)}
                    />
                </VersionedComponent>,
                <VersionedComponent version={VERSION_CM_14205_ASSET_STARRED} key={"Unstarred"}>
                    <TooltipButton
                        circled
                        title={t(`common::label::Unstarred`, `Unstarred`)}
                        buttonText={t(`common::label::Unstarred`, `Unstarred`)}
                        size={"small"}
                        onClickButton={() => {
                            onHandleClickStarred(
                                selectedItems.map((item) => item.id),
                                true,
                            );
                        }}
                        buttonIcon={<IconStarOutline size={16} />}
                        isDivided={false}
                        isResponsive={isResponsive}
                        disabled={!ASSETS_PERMISSIONS.READ}
                        hidden={!isCms || !selectedItems.some((item) => item.myFavorite)}
                    />
                </VersionedComponent>,
            ];
        },
        [
            assets,
            selectedItems,
            stage,
            ASSETS_PERMISSIONS,
            isCms,
            aiSearchEnabled,
            actionPending,
            onHandleClickMultiDownload,
            onHandleClickBulkReindex,
        ],
    );

    const getListToolsPopperButtons = useCallback(() => {
        const checkDisabledByOwnerLock = hasAnyOwnerLockBlocked(selectedItems, stage);

        return [
            [
                <ListItemButton
                    key="createCollection"
                    onClick={() => {
                        onToggleModal("createCollection", true);
                    }}
                    disabled={!ASSETS_PERMISSIONS.EDIT || checkDisabledByOwnerLock}
                    hidden={isCms}
                >
                    <ListItemText
                        primary={t("common::label::Create {{name}}", "Create {{name}}", {
                            name: t(`common::label::Collection`, `Collection`),
                        })}
                    />
                </ListItemButton>,
                <ListItemButton
                    key="shareLink"
                    onClick={() => {
                        onToggleModal("shareLink", true);
                    }}
                    disabled={!SHARES_PERMISSIONS.EXTERNAL || checkDisabledByOwnerLock}
                    hidden={isCms}
                >
                    <ListItemText primary={t("common::label::Share link", "Share link")} />
                </ListItemButton>,
                <ListItemButton
                    key="archive"
                    onClick={() => {
                        onToggleModal("archive", true);
                    }}
                    disabled={!ASSETS_PERMISSIONS.ARCHIVE || checkDisabledByOwnerLock}
                    hidden={isCms}
                >
                    <ListItemText primary={t("common::label::Archive", "Archive")} />
                </ListItemButton>,
                <ListItemButton
                    key="request"
                    onClick={() => {
                        onToggleModal("request", true);
                    }}
                    disabled={!SHARES_PERMISSIONS.INTERNALREQUEST}
                    hidden={compareVersions(stage.version, VERSION_CM_14143_REQUEST) < 0 || isCms}
                >
                    <ListItemText primary={t(`common::label::Request`, `Request`)} />
                </ListItemButton>,
                <ListItemButton
                    key="starred"
                    onClick={() => {
                        onHandleClickStarred(
                            selectedItems.map((item) => item.id),
                            false,
                        );
                    }}
                    disabled={!ASSETS_PERMISSIONS.READ}
                    hidden={
                        compareVersions(stage.version, VERSION_CM_14205_ASSET_STARRED) < 0 ||
                        !selectedItems.some((item) => !item.myFavorite) ||
                        isCms
                    }
                >
                    <ListItemText primary={t(`common::label::Starred`, `Starred`)} />
                </ListItemButton>,
                <ListItemButton
                    key="unstarred"
                    onClick={() => {
                        onHandleClickStarred(
                            selectedItems.map((item) => item.id),
                            true,
                        );
                    }}
                    disabled={!ASSETS_PERMISSIONS.READ}
                    hidden={
                        compareVersions(stage.version, VERSION_CM_14205_ASSET_STARRED) < 0 ||
                        !selectedItems.some((item) => item.myFavorite) ||
                        isCms
                    }
                >
                    <ListItemText primary={t(`common::label::Unstarred`, `Unstarred`)} />
                </ListItemButton>,
            ],
            [
                <ListItemButton
                    key="ChangeOwner"
                    onClick={() => {
                        onToggleModal("changeOwner", true);
                    }}
                    hidden={
                        compareVersions(stage.version, VERSION_CM_15199_MULTI_ASSETS_OWNER_CHANGE) < 0 ||
                        (!stage.user.isSpaceAdmin && !stage.projects.some((p) => p.id === projectId && p.isAdmin))
                    }
                >
                    <ListItemText primary={t(`common::label::Change Owner`, `Change Owner`)} />
                </ListItemButton>,
            ],
        ];
    }, [ASSETS_PERMISSIONS, stage, selectedItems, isCms]);

    const filteredAssetData = useMemo(() => {
        if (!assets?.data) return [];

        const filtered = assets.data.filter(({ archiveStatus }) => archiveStatus !== "REQUEST_ARCHIVE");

        // 유효한 정렬 컬럼이 없으면 정렬하지 않고 백엔드 순서를 보존한다.
        const sortColumn = AssetsColumns.find((column) => column.id === sortBy?.id && column.sortable);
        if (!sortColumn) return filtered;

        const { id, type } = sortColumn;
        const direction = sortBy?.isAsc ? 1 : -1;
        return [...filtered].sort((a, b) => {
            if (type === "number") {
                return direction * ((a[id] || 0) - (b[id] || 0));
            }
            return (
                direction *
                ((a[id]?.toLowerCase() || "").localeCompare(b[id]?.toLowerCase() || "", undefined, {
                    numeric: true,
                    sensitivity: "base",
                }) || 0)
            );
        });
    }, [assets, sortBy]);

    const pagination = useMemo(() => {
        if (
            mergeParams.activeView === LIST_VIEW_TYPE.SEARCH &&
            resourceSettings.settings?.Asset?.useSearchEngine?.value === "true"
        ) {
            return (
                <Pagination
                    totalCount={assets.totalCount}
                    page={mergeParams?.offset}
                    limitOptions={CONSTANTS("LIST_LIMITS")}
                    limit={mergeParams?.limit}
                    pageCount={assets.data?.length}
                    onChangePage={onChangeOffset}
                    onChangeLimit={onChangeLimit}
                    onClickRefresh={onClickRefresh}
                    hasNextToken={tokenPaginationInfo.data[mergeParams.offset]}
                />
            );
        } else {
            return (
                <Pagination
                    totalCount={assets.totalCount}
                    page={mergeParams?.offset / mergeParams?.limit}
                    limit={mergeParams?.limit}
                    pageCount={assets.data?.length}
                    onChangePage={(page) => onChangeOffset(page * mergeParams.limit)}
                    onChangeLimit={onChangeLimit}
                    onClickRefresh={onClickRefresh}
                    hasNextToken={mergeParams.offset < assets.totalCount - mergeParams?.limit}
                />
            );
        }
    }, [assets, mergeParams, filteredAssetData, resourceSettings, tokenPaginationInfo]);

    const assetBreadcrumbMenu = useMemo(() => {
        if (isCms) return [];

        return [
            {
                label: "New Folder",
                onClick: () => onToggleModal("createFolder", true),
                disabled: assets.hierarchyType !== GROUPING_VIEW_TYPE.FOLDER,
                buttonDisabled: !FOLDERS_PERMISSIONS.EDIT,
                clickClose: true,
            },
            {
                label: "Move",
                onClick: () => {
                    if (assets.hierarchyType === GROUPING_VIEW_TYPE.FOLDER) onToggleModal("moveFolder", true);
                    else onToggleModal("moveCategory", true);
                },
                clickClose: true,
                divider: assets.hierarchyType === GROUPING_VIEW_TYPE.FOLDER,
                disabled: folderTreeView.folderList.selectedFolder.folder?.isRoot,
                buttonDisabled: !ASSETS_PERMISSIONS.EDIT,
            },
            {
                label: "Rename",
                onClick: () => onToggleModal("renameFolder", true, folderTreeView.folderList.selectedFolder.folder),
                clickClose: true,
                disabled:
                    folderTreeView.folderList.selectedFolder.folder?.isRoot ||
                    assets.hierarchyType !== GROUPING_VIEW_TYPE.FOLDER,
                buttonDisabled: !FOLDERS_PERMISSIONS.EDIT,
            },
            {
                label: "Create Collection",
                onClick: () => onToggleModal("createCollectionFolder", true),
                clickClose: true,
                disabled:
                    folderTreeView.folderList.selectedFolder.folder?.isRoot ||
                    assets.hierarchyType !== GROUPING_VIEW_TYPE.FOLDER,
                buttonDisabled: !ASSETS_PERMISSIONS.EDIT,
            },
            {
                label: "Archive",
                onClick: () => onToggleModal("archiveFolder", true),
                clickClose: true,
                divider: true,
                disabled:
                    folderTreeView.folderList.selectedFolder.folder?.isRoot ||
                    assets.hierarchyType !== GROUPING_VIEW_TYPE.FOLDER,
                buttonDisabled: !ASSETS_PERMISSIONS.ARCHIVE,
            },
        ];
    }, [folderTreeView.folderList.selectedFolder.folder, assets, stage, ASSETS_PERMISSIONS, FOLDERS_PERMISSIONS]);

    // 뷰 정보에서 정렬 상태를 초기화하는 함수를 분리
    const initializeSortState = () => {
        const defaultSort = { id: null, isAsc: false };
        if (!spaceId || !projectId) return defaultSort;
        const viewInfo = loadViewInfo("assetsViewInfo", spaceId, projectId);
        const { sortBy, isSortAsc } = viewInfo || {};

        // persisted 정렬 컬럼이 실제 sortable 컬럼일 때만 반영 (스테일/무효 값 차단)
        const isValidSortColumn = AssetsColumns.some((column) => column.id === sortBy && column.sortable);
        return {
            id: isValidSortColumn ? sortBy : null,
            isAsc: isSortAsc || false,
        };
    };

    const isThumbnailCropped = useMemo(() => {
        return resourceSettings.settings?.Asset?.thumbnailCropOption?.value === "true";
    }, [resourceSettings]);

    // 프록시를 만들지 않는 프로젝트에서는 프록시 배지를 노출하지 않는다 (DAM-444)
    const isProxyVideoEnabled = useMemo(() => {
        return resourceSettings.settings?.Asset?.isCreateVideoPreview?.value === "true";
    }, [resourceSettings]);

    const hiddenSearchBarParams = useMemo(() => {
        if (isCustomMenu === undefined) return null;
        let hiddenParams = [];
        if (isCustomMenu) {
            if (searchQueryParams.params.selectedContentSchemaId) hiddenParams.push("contentSchema");
            else if (searchQueryParams.params.selectedCategoryId) hiddenParams.push("categoryIds");
            else if (searchQueryParams.params.virtualFolderId) hiddenParams.push("location");
            return hiddenParams.length > 0 ? hiddenParams : null;
        }
        return hiddenParams;
    }, [isCustomMenu, searchQueryParams.params]);

    const hiddenSearchFilterParams = useMemo(() => {
        if (isCustomMenu === undefined) return null;
        let hiddenParams = [];
        if (isCustomMenu) {
            if (searchQueryParams.params.selectedContentSchemaId) hiddenParams.push("contentSchema");
            else if (searchQueryParams.params.selectedCategoryId) hiddenParams.push("categoryIds");
            else if (searchQueryParams.params.virtualFolderId) {
                hiddenParams.push("location");
                if (window.location.hostname.includes("lxhausys.")) {
                    hiddenParams.push("categoryIds");
                }
            }
            return hiddenParams;
        }
        return hiddenParams;
    }, [isCustomMenu, searchQueryParams.params]);

    const initCustomMenuParams = useCallback(
        (params) => {
            try {
                let inputParams = {
                    ...(params ?? searchQueryParams.params),
                    offset: searchQueryParams.params.offset ?? 0,
                };

                let query = location.search;
                if (query) {
                    if (query.startsWith("?")) {
                        query = query.slice(1);
                    }

                    const parseValue = (decodedValue) => {
                        try {
                            const parsedValue = safeJsonParse(decodedValue);
                            return parsedValue;
                        } catch (e) {
                            return decodedValue;
                        }
                    };

                    let result = {};
                    // '&'로 나눠서 각 key=value 처리
                    query.split("&").forEach((pair) => {
                        if (pair) {
                            const [key, value] = pair.split("=");
                            const decodedValue = decodeURIComponent(value);
                            const parsedValue = parseValue(decodedValue);
                            if (typeof parsedValue === "object") result = { ...result, ...parsedValue };
                            else result = { ...result, [key]: decodedValue };
                        }
                    });

                    if (!["search", "list"].includes(inputParams.activeView)) {
                        if (result.enableCategoryView || result.enableFolderView) {
                            result.activeView = "hierarchy";
                            if (result.enableFolderView) result.hierarchyType = "folder";
                            else result.hierarchyType = "category";
                        }
                    }

                    inputParams = { ...inputParams, ...result };

                    dispatch(
                        updateState({
                            ...inputParams,
                        }),
                    );
                    searchQueryParams.resetSearchQueryParams({
                        action: "replace",
                        params: inputParams,
                    });
                }
            } catch (error) {
                console.log(error);
            }
        },
        [settingsMenuName],
    );

    const isQueryParamsReady = useMemo(() => {
        if (!ASSETS_PERMISSIONS.READ || !resourceSettings.settings || !settingsMenuName.settings) return false;
        if (isCustomMenu) {
            if (
                !(
                    mergeParams.selectedCategoryId ||
                    mergeParams.selectedContentSchemaId ||
                    mergeParams.virtualFolderId ||
                    mergeParams.hasOwnProperty("enableFolderView") ||
                    mergeParams.hasOwnProperty("enableCategoryView")
                )
            )
                return false;
        }

        if (!isCustomMenu && !mergeParams.activeView) return false;

        return true;
    }, [mergeParams, resourceSettings, settingsMenuName]);

    useEffect(() => {
        getFilterOptions(["extensions", "contentSchema"]);
        getSettingData("Asset");
        getSettingMenuName("MenuName");
        setSortBy(initializeSortState());

        sessionStorage.removeItem(`imagesViewer`);
        window.addEventListener("scroll", handleScroll);
        return () => {
            dispatch(clear());
            window.removeEventListener("scroll", handleScroll);
            dispatch(closePropertyPanel());
            clearTimeout(reindexRefreshTimerRef.current);
            // 재시도 응답이 언마운트 직후 도착하면 cleanup 이후에 타이머가 새로 예약된다.
            // 그 타이머는 여기서 취소할 수 없으므로 지연 새로고침 자체를 무력화한다.
            onClickRefreshRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (selectedItems?.length > 0) {
            setSelectedItems([]);
            dispatch(clearList());
        }
    }, [assets.isActiveView]);

    useEffect(() => {
        if (!isQueryParamsReady) return;

        if (
            (mergeParams.activeView &&
                ![LIST_VIEW_TYPE.SEARCH, LIST_VIEW_TYPE.HIERARCHY, LIST_VIEW_TYPE.LIST].includes(
                    mergeParams.activeView,
                )) ||
            (mergeParams.hierarchyType &&
                ![GROUPING_VIEW_TYPE.CATEGORY, GROUPING_VIEW_TYPE.FOLDER].includes(mergeParams.hierarchyType))
        ) {
            searchQueryParams.resetSearchQueryParams({ action: "push" });
            return;
        }
        onChangeSearchQueryParams();
    }, [mergeParams, isQueryParamsReady]);

    useEffect(() => {
        if (!resourceSettings.settings || !settingsMenuName.settings) return;
        const listConditionValue = resourceSettings?.settings?.Asset?.listCondition?.value;
        const listCondition = listConditionValue ? safeJsonParse(listConditionValue) : null;
        let params = { ...searchQueryParams.params };
        if (listCondition?.isNotIngestType) params.isNotIngestType = listCondition.isNotIngestType;

        if (isCustomMenu) {
            initCustomMenuParams(params);
        } else {
            initActiveViewParams(params);
        }
    }, [resourceSettings, settingsMenuName]);

    // AI 검색(V3 ASSET 도메인) 등록 여부 조회 - AI 모드 노출/페이지 판단용. pending 플래그로 조회 완료 시점 파생
    useEffect(() => {
        if (!projectId || isCms) return;
        if (compareVersions(stage.version, VERSION_CM_16963_PROJECT_SETTING_AI_SEARCH) < 0) return;
        // poll: false - 목록에선 1회 조회만(동기화 중 폴링으로 버튼이 갑자기 노출되는 것 방지)
        dispatch(listRegisteredDomains("ASSET", projectId, { poll: false })).catch(() => {});
    }, [projectId, stage.version, isCms]);

    const isAutoArchiveEnabled = !!resourceSettings?.settings?.Asset?.autoArchiveOption?.value?.isEnabled;

    const assetTableColumns = useMemo(() => {
        const isIndexStatusVisible =
            aiSearchEnabled && compareVersions(stage.version, VERSION_DAM_28_ASSET_INDEX_STATUS) >= 0;
        return AssetsColumns.filter((column) => {
            if (column.id === "autoArchivedAt") return isAutoArchiveEnabled;
            if (column.id === "indexStatus") return isIndexStatusVisible;
            return true;
        });
    }, [isAutoArchiveEnabled, stage.version, aiSearchEnabled]);

    const onClickDeleteAssetRow = useCallback((v) => onToggleModal("delete", true, v), [onToggleModal]);

    const isDraggableByHierarchy = useMemo(
        () =>
            assets?.isActiveView?.toLowerCase() === LIST_VIEW_TYPE.HIERARCHY &&
            (assets?.hierarchyType === GROUPING_VIEW_TYPE.CATEGORY ||
                assets?.hierarchyType === GROUPING_VIEW_TYPE.FOLDER) &&
            ASSETS_PERMISSIONS.EDIT &&
            !(assets?.hierarchyType === GROUPING_VIEW_TYPE.CATEGORY && selectedItemsHasOwnerLock),
        [ASSETS_PERMISSIONS.EDIT, assets?.hierarchyType, assets?.isActiveView, selectedItemsHasOwnerLock],
    );

    const createAssetInitialValues = useMemo(() => {
        const initialValues = { initialFolder: currentFolderInfo };

        if (isCustomMenu) {
            if (
                searchQueryParams.params.hasOwnProperty("enableFolderView") ||
                searchQueryParams.params.hasOwnProperty("enableCategoryView")
            ) {
                if (mergeParams.selectedCategoryId) {
                    const selectedCategory = categoryTreeView.categoryMap[mergeParams.selectedCategoryId];
                    const selectedCategoryGroup = categoryTreeView.categoryMap[selectedCategory?.categoryGroup?.id];

                    if (selectedCategory?.id !== UNCATEGORIZED_ID && selectedCategory?.type === "category") {
                        initialValues["initialCategory"] = {
                            categoryId: selectedCategory?.id,
                            categoryName: selectedCategory?.name,
                            categoryGroupId: selectedCategoryGroup?.id,
                            categoryGroupName: selectedCategoryGroup?.name,
                        };
                    }
                }
                if (mergeParams.selectedContentSchemaId) {
                    initialValues["initialCustomMetadata"] = {
                        customMetadataId: mergeParams.selectedContentSchemaId,
                    };
                }
            } else if (mergeParams.virtualFolderId && mergeParams.virtualDriveId) {
                initialValues["initialFolder"] = {
                    folderId: mergeParams.virtualFolderId,
                    driveId: mergeParams.virtualDriveId,
                };
            } else if (mergeParams.selectedCategoryId) {
                initialValues["initialCategory"] = {
                    categoryId: mergeParams?.selectedCategoryId,
                    categoryGroupId: mergeParams?.defaultCategoryGroupId,
                };
            }
        }

        return initialValues;
    }, [
        location.pathname,
        mergeParams,
        currentFolderInfo,
        assets.isActiveView,
        assets.hierarchyType,
        categoryTreeView.categoryMap,
        settingsMenuName,
    ]);

    return (
        <React.Fragment>
            {!ASSETS_PERMISSIONS.READ || assets.error ? ( // status 저장 후 403이면 뜨도록 처리
                <AccessDenied />
            ) : (
                <>
                    {modal === "create" && (
                        <DialogCreateAsset open={true} onClose={() => onToggleModal()} {...createAssetInitialValues} />
                    )}
                    <PageHeader
                        title={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                                {isQueryParamsReady && (
                                    <>
                                        {isCustomMenu ? (
                                            <CustomMenuBreadcrumb />
                                        ) : (
                                            <Typography variant={"h3"} color={theme.palette.greySecondary.main}>
                                                {t(`common::label::Assets`)}
                                            </Typography>
                                        )}
                                    </>
                                )}
                            </Box>
                        }
                        breadcrumb={
                            compareVersions(stage.version, VERSION_CM_12923_LIST_SEARCHBAR_FILTER) < 0 && (
                                <BreadcrumbsWithRouterPath />
                            )
                        }
                        actions={
                            !isCms &&
                            compareVersions(stage.version, VERSION_CM_12725_ASSET_AUTO_ARCHIVE) >= 0 &&
                            resourceSettings?.settings?.Asset?.autoArchiveOption?.value?.isEnabled && (
                                <Tooltip
                                    title={t(`common::label::Auto-archive enabled`, `Auto-archive enabled`)}
                                    placement={"top"}
                                    PopperProps={{
                                        // 실효 간격 6px = 툴팁 마진 4px + 2. 마진이 14px 이던 때의 -8 을 옮긴 값이다(theme MuiTooltip).
                                        modifiers: [{ name: "offset", options: { offset: [0, 2] } }],
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.5,
                                            px: 2,
                                            height: 32,
                                            mt: -1,
                                            backgroundColor: alpha(theme.palette.greySecondary[50], 0.5),
                                            borderRadius: 100,
                                        }}
                                    >
                                        <IconCircle size={14} htmlColor={theme.palette.success.main} />
                                        <IconArchive size={20} htmlColor={theme.palette.greySecondary[500]} />
                                    </Box>
                                </Tooltip>
                            )
                        }
                    />
                    <PageTools
                        search={
                            <AssetSearchBar
                                domain={TABLE_LIST_TYPE.ASSET}
                                aiModeSlot={
                                    aiSearchEnabled && (
                                        <AiWorkspaceButton
                                            startIcon={<IconAi size={16} />}
                                            onClick={onClickEnterAiSearch}
                                        >
                                            {t(`common::label::AI Mode`, `AI Mode`)}
                                        </AiWorkspaceButton>
                                    )
                                }
                                hiddenSearchBarParams={hiddenSearchBarParams}
                                hiddenSearchFilterParams={hiddenSearchFilterParams}
                                isSearchEngine={resourceSettings.settings?.Asset?.useSearchEngine?.value === "true"}
                                aiSearchEnabled={aiSearchEnabled}
                                projectId={projectId}
                                initialValues={searchBarInitialValues}
                                isQuickSearch={isQuickSearch}
                                optionValues={filterOptionValues}
                                onChangeQuickSearch={() => {
                                    setIsQuickSearch(!isQuickSearch);
                                }}
                                onClickReset={() => {
                                    const viewInfo = getViewInfoParams();
                                    const params = searchQueryParams.params;
                                    const { isNotIngestType } = params;
                                    const _isNotIngestType = isNotIngestType ? { isNotIngestType } : {};
                                    if (isCustomMenu) {
                                        initCustomMenuParams({ activeView: viewInfo.activeView, ..._isNotIngestType });
                                    } else {
                                        searchQueryParams.resetSearchQueryParams({
                                            action: "push",
                                            params: {
                                                isQuickSearch: params.isQuickSearch,
                                                limit: params.limit,
                                                activeView: viewInfo.activeView,
                                                hierarchyType: viewInfo.hierarchyType,
                                                selectedCategoryId: viewInfo.selectedCategoryId,
                                                virtualFolderId: viewInfo.virtualFolderId,
                                                virtualDriveId: viewInfo.virtualDriveId,
                                                ...(compareVersions(
                                                    stage.version,
                                                    VERSION_CM_12725_ASSET_AUTO_ARCHIVE,
                                                ) >= 0
                                                    ? { withAutoArchiveDate: true }
                                                    : {}),
                                                ..._isNotIngestType,
                                            },
                                        });
                                    }
                                }}
                                onClickSubmit={(values) => {
                                    dispatch(closePropertyPanel());
                                    onClickActiveView({ isActiveView: LIST_VIEW_TYPE.SEARCH });
                                    searchQueryParams.resetSearchQueryParams({
                                        action: "push",
                                        params: {
                                            ...values,
                                            activeView: LIST_VIEW_TYPE.SEARCH,
                                            keyword: isQuickSearch ? values.keyword : values.name,
                                            name: isQuickSearch ? values.keyword : values.name,
                                        },
                                    });
                                }}
                                currentCategoryGroup={resourceSettings.settings?.Asset?.categoryGroupId}
                            />
                        }
                        actions={
                            <Grid container flexDirection={"row"} gap={1}>
                                {isCreateButtonVisible && (
                                    <Grid>
                                        <Button
                                            size="medium"
                                            startIcon={<IconAdd size={20} />}
                                            variant="contained"
                                            onClick={onHandleClickCreate}
                                            disabled={!ASSETS_PERMISSIONS.EDIT}
                                        >
                                            <Typography variant="body1_700">
                                                {t(`common::label::${("Create", "Create")}`)}
                                            </Typography>
                                        </Button>
                                    </Grid>
                                )}
                            </Grid>
                        }
                    />
                    <Divider flexItem />

                    <Grid
                        container
                        sx={{ flex: 1, overflow: `hidden`, position: `relative` }}
                        flexWrap={"nowrap"}
                        gap={3}
                    >
                        <CollapsibleSplitTwoTierGridLeft
                            gridLeftProps={{ minSize: 336, maxSize: 740, defaultSize: 336 }}
                            gridLeft={
                                searchQueryParams.params.activeView?.toLowerCase() === LIST_VIEW_TYPE.HIERARCHY && (
                                    // TODO : Dev, Demo, KBTV 분기처리, 추 후 조건 삭제
                                    <Grid item sx={{ height: "100%" }}>
                                        <TreeViewLayout
                                            popperMenu={true}
                                            driveDomain={"Asset"}
                                            resourceType={"Asset"}
                                            searchQueryParams={searchQueryParams}
                                            isCustomMenu={isCustomMenu}
                                            onDropTreeItem={onDropTreeItem}
                                            onClickTreeItem={onClickTreeItem}
                                            onClickForderTree={onHandleClickFolderTree}
                                            onClickCreateNewFolder={onClickCreateNewFolder}
                                            updateActiveView={onClickActiveView}
                                            onRefresh={onClickRefresh}
                                            isFolderDndEnabled={
                                                compareVersions(stage.version, VERSION_DAM_228_FOLDER_TREE_DND) >= 0 &&
                                                !isCms
                                            }
                                        />
                                    </Grid>
                                )
                            }
                            gridRight={
                                <Grid
                                    item
                                    xs
                                    sx={{
                                        display: `flex`,
                                        flexDirection: `column`,
                                        overflow: `hidden`,
                                        height: "100%",
                                    }}
                                >
                                    <ListTools
                                        domain={TABLE_LIST_TYPE.ASSET}
                                        isCardView={isCardView}
                                        getButtons={getListToolsButtons}
                                        pageCount={filteredAssetData?.filter(({ state }) => state !== "DRAFT")?.length}
                                        numSelected={selectedItems?.length}
                                        filter={null}
                                        onToggleViewType={onClickViewType}
                                        onChangeNumSelected={onChangeNumSelected}
                                        pagination={pagination}
                                        treeViewProps={
                                            searchQueryParams.params.activeView ||
                                            searchQueryParams.params.activeView === "search"
                                                ? {
                                                      activeView:
                                                          searchQueryParams.params.activeView || assets.isActiveView,
                                                      hierarchyType:
                                                          assets.hierarchyType ??
                                                          searchQueryParams.params.hierarchyType,
                                                      viewSelectButton: (
                                                          <ListViewSelectButton
                                                              domain={"Asset"}
                                                              isActiveView={
                                                                  searchQueryParams.params.activeView ||
                                                                  assets.isActiveView
                                                              }
                                                              updateActiveView={onClickActiveView}
                                                          />
                                                      ),
                                                      breadcrumbMenu: assetBreadcrumbMenu,
                                                      onClickFolderTree: onHandleClickFolderTree,
                                                      onClickTreeItem,
                                                      onToggleModal,
                                                  }
                                                : undefined
                                        }
                                        sortOptions={AssetsColumns.filter((el) => el.sortable)}
                                        onChangeSort={(id) => onHandleChangeSort({ id })}
                                        onChangeSortAsc={(isAsc) => onHandleChangeSort({ isAsc })}
                                        getPopperButtons={getListToolsPopperButtons}
                                    />

                                    {assets.pending || !assets.data ? (
                                        <Grid item xs sx={{ height: "100%" }}>
                                            <Grid
                                                container
                                                alignItems={"center"}
                                                justifyContent={"center"}
                                                sx={{ height: "100%" }}
                                            >
                                                <CircularProgress color="grey" />
                                            </Grid>
                                        </Grid>
                                    ) : (
                                        <React.Fragment>
                                            {selectedItems && selectedItems.length ? (
                                                <ListDragPreview
                                                    ref={listDragPreviewRef}
                                                    count={selectedItems.length}
                                                    selectedItem={selectedItems[0]}
                                                />
                                            ) : (
                                                ""
                                            )}
                                            {isCardView ? (
                                                <React.Fragment>
                                                    <Grid
                                                        container
                                                        gap={2.5}
                                                        sx={{
                                                            maxHeight: `100%`,
                                                            overflowY: "auto",
                                                            scrollbarGutter: "stable",
                                                        }}
                                                    >
                                                        {filteredAssetData?.length > 0 ? (
                                                            <React.Fragment>
                                                                {filteredAssetData?.map((v) => (
                                                                    //{/* new class : is-new */}
                                                                    <AssetCard
                                                                        key={v.id}
                                                                        asset={v}
                                                                        checkable
                                                                        checked={
                                                                            !!selectedItems.find(
                                                                                (item) => item.id === v.id,
                                                                            )
                                                                        }
                                                                        draggable={
                                                                            isDraggableByHierarchy &&
                                                                            selectedItems.includes(v)
                                                                        }
                                                                        thumbnailCrop={isThumbnailCropped}
                                                                        isProxyVideoEnabled={isProxyVideoEnabled}
                                                                        onDragStartItem={onDragStartItem}
                                                                        onDragEndItem={onDragEndItem}
                                                                        onChangeCheck={onChangeSelectItem}
                                                                        onClickRow={onClickRow}
                                                                        onDoubleClickRow={onDoubleClickRow}
                                                                        onClickDeleteAsset={onClickDeleteAssetRow}
                                                                        onClickCreateJob={onClickCreateJob}
                                                                        hiddenCreateJobButton={hideContents}
                                                                        onToggleModal={onToggleModal}
                                                                        onClickName={getLinkDestination}
                                                                        onClickDownload={onHandleClickDownload}
                                                                        onClickStarred={onHandleClickStarred}
                                                                        onClickOwnerLock={onClickOwnerLock}
                                                                        showReindex={isRowReindexVisible}
                                                                        onClickReindex={onClickRowReindex}
                                                                    />
                                                                ))}
                                                            </React.Fragment>
                                                        ) : (
                                                            <React.Fragment>
                                                                <div className={"empty"}>
                                                                    <i className={"sprite sprite-nojob"} />
                                                                    <p>
                                                                        {t(`common::label::Empty {{name}}`, {
                                                                            name: t(`common::label::Assets`),
                                                                        })}
                                                                    </p>
                                                                </div>
                                                            </React.Fragment>
                                                        )}
                                                    </Grid>
                                                </React.Fragment>
                                            ) : (
                                                <Grid
                                                    item
                                                    xs
                                                    sx={{
                                                        overflow: `hidden`,
                                                    }}
                                                >
                                                    <AssetTable
                                                        data={filteredAssetData}
                                                        columns={assetTableColumns}
                                                        draggable={isDraggableByHierarchy && selectedItems?.length > 0}
                                                        selectedItemsDragging={selectedItemsDragging}
                                                        selectedItems={selectedItems}
                                                        thumbnailCrop={isThumbnailCropped}
                                                        isProxyVideoEnabled={isProxyVideoEnabled}
                                                        onDragStartItem={onDragStartItem}
                                                        onClickRow={onClickRow}
                                                        onDoubleClickRow={onDoubleClickRow}
                                                        onDragEndItem={onDragEndItem}
                                                        onChangeSelectItem={onChangeSelectItem}
                                                        onClickToggleActionButton={onClickToggleActionButton}
                                                        onClickDeleteButton={onClickDeleteAssetRow}
                                                        onClickCreateJob={onClickCreateJob}
                                                        onClickDownload={onHandleClickDownload}
                                                        onToggleModal={onToggleModal}
                                                        hiddenCreateJobButton={hideContents}
                                                        useAutoArchive={isAutoArchiveEnabled}
                                                        onClickName={getLinkDestination}
                                                        onClickStarred={onHandleClickStarred}
                                                        onClickOwnerLock={onClickOwnerLock}
                                                        showReindex={isRowReindexVisible}
                                                        onClickReindex={onClickRowReindex}
                                                    />
                                                </Grid>
                                            )}
                                        </React.Fragment>
                                    )}
                                </Grid>
                            }
                        />
                    </Grid>
                </>
            )}
            {/*콜렉션 생성*/}
            {modal === "createCollection" && (
                <DialogCreateCollection
                    onClose={() => setModal("")}
                    open={modal === "createCollection"}
                    assetItems={selectedItems}
                />
            )}{" "}
            {/*콜렉션 생성(폴더 브레드크럼)*/}
            {modal === "createCollectionFolder" && (
                <DialogCreateCollection
                    onClose={() => setModal("")}
                    open={modal === "createCollectionFolder"}
                    assetFolder={folderTreeView.folderList.selectedFolder.folder}
                />
            )}
            {/* 복사 */}
            {modal === "copyCategory"
                ? ReactDOM.createPortal(
                      <LoadableSelectCategoriesModal
                          submitText={`Copy to`}
                          title={`Copy to`}
                          type={CATEGORY_TYPES.ASSET}
                          onModalClose={() => {
                              onToggleModal();
                          }}
                          onSelectedCategories={async (option) => {
                              onToggleModal();
                              await onChangeCategoriesCopyToItems(option);
                              await afterMutationWorks();
                          }}
                      />,
                      document.body,
                  )
                : ""}
            {/* 이동 */}
            {modal === "moveCategory" &&
                ReactDOM.createPortal(
                    <LoadableSelectCategoriesModal
                        submitText={`Move`}
                        title={`Move`}
                        type={CATEGORY_TYPES.ASSET}
                        onModalClose={() => {
                            onToggleModal();
                        }}
                        onSelectedCategories={async (option) => {
                            onToggleModal();
                            await onChangeCategoriesMoveToItems(option);
                            await afterMutationWorks();
                        }}
                    />,
                    document.body,
                )}
            {modal === "moveFolder" &&
                ReactDOM.createPortal(
                    <LoadableSelectFolderPathModal
                        open
                        domain={"Asset"}
                        submitText={`Move`}
                        title={`Move`}
                        onSubmit={({ folderId }) => {
                            if (selectedItems.length > 0 || actionTarget) onHandleClickChangeAssetsFolder(folderId);
                            else onChangeFolderPath();
                        }}
                        onClose={() => {
                            onToggleModal();
                        }}
                    />,
                    document.body,
                )}
            {/* 새폴더 */}
            {modal === "createFolder" &&
                ReactDOM.createPortal(
                    <LoadableNewFolderModal
                        folder={folderTreeView.folderList.selectedFolder.folder}
                        onClickClose={() => {
                            onToggleModal("createFolder");
                        }}
                        onClickSubmit={onClickCreateNewFolder}
                    />,
                    document.body,
                )}
            {modal === "share" && (
                <DialogShare domain={"ASSET"} selectedItem={actionTarget[0]} onClickClose={() => onToggleModal()} />
            )}
            {modal === "shareLink" &&
                ReactDOM.createPortal(
                    <LoadableDownloadLinkModal
                        domain={"ASSET"}
                        selectedItems={actionTarget ? actionTarget : selectedItems}
                        onClickClose={() => onToggleModal()}
                        onSubmit={() => {
                            onToggleModal();
                            afterMutationWorks();
                        }}
                    />,
                    document.body,
                )}
            {modal === "request" && (
                <DialogRequest
                    open={modal === "request"}
                    requirePermissionCheck={false}
                    projectId={projectId}
                    assets={actionTarget ?? selectedItems}
                    onClose={() => onToggleModal()}
                />
            )}
            {modal === "rename" &&
                ReactDOM.createPortal(
                    <LoadableRenameModal
                        selectedItems={actionTarget ? actionTarget : selectedItems}
                        onClickClose={() => onToggleModal()}
                        onClickSubmit={handleClickRenameAsset}
                        afterMutationWorks={afterMutationWorks}
                    />,
                    document.body,
                )}
            {modal === "archive" && (
                <DialogArchive
                    active={true}
                    onClose={() => onToggleModal()}
                    onSubmit={onHandleClickArchiveAsset}
                    actionPending={actionPending.archive}
                    data={selectedItems}
                    isMulti={selectedItems.length > 1}
                />
            )}
            {modal === "archiveFolder" && (
                <DialogArchive
                    active={true}
                    onClose={() => onToggleModal()}
                    onSubmit={onHandleClickArchiveAsset}
                    actionPending={actionPending.archive}
                    folder={folderTreeView.folderList.selectedFolder.folder}
                    isMulti={selectedItems.length > 1}
                />
            )}
            {modal === "delete" &&
                ReactDOM.createPortal(
                    <LoadableDeleteAssetWithReferenceModal
                        activeView={assets.isActiveView}
                        hierarchyCategorySelected={
                            (isCustomMenu &&
                                searchQueryParams.params.selectedCategoryId &&
                                !searchQueryParams.params.activeView) ||
                            assets.isActiveView === LIST_VIEW_TYPE.LIST ||
                            !searchQueryParams.params.selectedCategoryId ||
                            searchQueryParams.params.selectedCategoryId === UNCATEGORIZED_ID
                                ? {}
                                : searchQueryParams.params.selectedCategoryId
                        }
                        selectedItems={
                            actionTarget
                                ? [
                                      {
                                          id: actionTarget[0].id,
                                          name: actionTarget[0].name,
                                          version: actionTarget[0].version,
                                      },
                                  ]
                                : selectedItems.map((item) => ({
                                      id: item.id,
                                      name: item.name,
                                      version: item.version,
                                  }))
                        }
                        onClickClose={() => onToggleModal()}
                        onClickDelete={onClickDeleteWithReference}
                        onClickDeleteAll={onClickDeleteAll}
                        onDeleteEnd={onDeleteEnd}
                        isCategoryDelete={
                            !(
                                isCustomMenu &&
                                searchQueryParams.params.selectedCategoryId &&
                                !searchQueryParams.params.activeView
                            ) &&
                            assets.isActiveView?.toLowerCase() === LIST_VIEW_TYPE.HIERARCHY &&
                            searchQueryParams.params.selectedCategoryId &&
                            searchQueryParams.params.selectedCategoryId !== UNCATEGORIZED_ID
                        }
                    />,
                    document.body,
                )}
            {modal === "createPhoto" &&
                ReactDOM.createPortal(
                    <LoadableCreatePhotoModal
                        selectedItems={selectedItems}
                        onClickClose={() => onToggleModal()}
                        onSubmit={() => {
                            onToggleModal();
                        }}
                    />,
                    document.body,
                )}
            {modal === "renameFolder" &&
                ReactDOM.createPortal(
                    <LoadableFolderRenameModal
                        folder={folderTreeView.folderList.selectedFolder.folder}
                        onClickClose={() => onToggleModal()}
                        onClickSubmit={onRenameFolder}
                        afterMutationWorks={afterMutationWorks}
                    />,
                    document.body,
                )}
            {modal === "requestShare" &&
                ReactDOM.createPortal(
                    <LoadableShareRequestModal
                        selectedItem={{ domain: "asset", ...actionTarget?.[0] }}
                        onClickClose={() => onToggleModal()}
                    />,
                    document.body,
                )}
            {modal === "requestDownload" && (
                <DialogRequestDownload projectId={projectId} assets={actionTarget} onClose={() => onToggleModal()} />
            )}
            {/* 복제 */}
            {modal === "duplicate" && (
                <DialogAssetDuplicate
                    isMulti={true}
                    assets={actionTarget ? actionTarget : selectedItems}
                    onClickClose={() => onToggleModal()}
                    isCustomMenu={isCustomMenu}
                    isCms={isCms}
                />
            )}
            {/* 소유자 변경 */}
            {modal === "changeOwner" && (
                <DialogChangeOwner
                    assets={actionTarget ? actionTarget : selectedItems}
                    onClose={() => onToggleModal()}
                    afterMutationWorks={afterMutationWorks}
                />
            )}
        </React.Fragment>
    );
};

let AssetList_ = AssetList;
AssetList_ = withSearchQueryParamsContext({
    addedSchema: {
        offset: yup.number().default(0),
        isQuickSearch: yup.bool().default(true),
        videoId: yup.string(),
        peopleId: yup.string(),
        extensions: yup
            .array()
            .of(yup.string().transform((value) => !fp.isEmpty(value) && value.toLowerCase()))
            .nullable()
            .default(undefined),
        indexDocStatus: yup
            .array()
            .of(yup.string().transform((value) => (typeof value === "string" ? value.toUpperCase() : value)))
            .nullable()
            .default(undefined),
        ingestType: yup
            .string()
            .transform((value) => !fp.isNil(value) && value.toUpperCase())
            .oneOf(getConstantValues("ASSET_INGEST_TYPES")),
        isNotIngestType: yup
            .string()
            .transform((value) => !fp.isNil(value) && value.toUpperCase())
            .oneOf(getConstantValues("ASSET_INGEST_TYPES")),
        activeView: yup.string(),
        selectedCategoryId: yup.string(),
        withAutoArchiveDate: yup.boolean(),
        enableFolderView: yup.boolean(),
        enableCategoryView: yup.boolean(),
        categoryGroup: yup.object(),
        contentSchema: yup
            .object({
                id: yup.string(),
                value: yup.string(),
                label: yup.string(),
            })
            .nullable()
            .default(undefined),
        defaultCategoryGroupId: yup.string(), //Note: Custom menu에서 카테고리 메뉴를 만들경우 생성모달에 기본값으로 전달할 카테고리 그룹을 위해 사용(categoryGroup은 목록조회 API에서 조건이 붙어 파라미터가 달라질 수 있어 사용불가)
    },
    viewInfoKey: "assetsViewInfo",
    setProps: true,
    // removeExtraParams: true,
})(AssetList_);

export default AssetList_;
